import React, { useState, useEffect, useCallback, useMemo } from "react";
import { loadJsonData } from "../utils/hybridDataLoader";
import { Container, Box, Chip, Autocomplete, TextField, Stack, CircularProgress, Typography } from "@mui/material";
import { defaultGroupForType, getGroupChips } from "./hooks/fantasyGroups";
import { useSmartFantasyData } from "./hooks/useSmartFantasyData";
import { renderSelectedFantasyTable } from "./hooks/renderSelectedFantasyTable";
import GroupSelector from "./hooks/groupSelector";
import { trackFantasyAction, trackDriverSelection } from "../../../utils/analytics";
import { useHashParam, readHashParam, writeHashParam, oneOf } from "../../../utils/hashParams";

const FANTASY_TABLES = ["overview", "finish", "start", "points", "rating", "advanced", "ranking", "consistency"];
const MAX_MANUAL_DRIVERS = 4;

// A shared link names the drivers of a manual comparison as plain names, separated by "|"
const linkedManualDrivers = () => {
  const raw = readHashParam("compare");
  return raw ? raw.split("|").filter(Boolean).slice(0, MAX_MANUAL_DRIVERS) : null;
};

// Helper function to extract driver name from formatted string
const extractDriverName = (formattedName) => {
  if (formattedName.startsWith("#")) {
    return formattedName.replace(/^#\d+\s/, "");
  }
  return formattedName;
};

const FantasyMainScreen = () => {
  const [nextRaceTrack, setNextRaceTrack] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // What is selected here lives in the URL too, so a view can be shared as a link
  const [groupMode, setGroupMode] = useHashParam("mode", "star", { parse: oneOf(["star", "open"]) });
  const useStarGroup = groupMode === "star";
  const [selectedGroup, setSelectedGroup] = useHashParam("groups", [defaultGroupForType[groupMode]], {
    parse: (raw) => {
      const groups = raw.split(",");
      const valid = (groups.length === 1 && groups[0] === "All")
        || groups.every((group) => getGroupChips(useStarGroup).includes(group));
      return valid ? groups : undefined;
    },
    serialize: (groups) => groups.join(","),
  });
  const [selectedTable, setSelectedTable] = useHashParam("view", "finish", { parse: oneOf(FANTASY_TABLES) });

  // Switching between Star and Open starts again from that mode's default group
  const setUseStarGroup = useCallback((star) => {
    const mode = star ? "star" : "open";
    if (mode === groupMode) return;
    setGroupMode(mode);
    setSelectedGroup([defaultGroupForType[mode]]);
  }, [groupMode, setGroupMode, setSelectedGroup]);

  const [manualCompareMode, setManualCompareMode] = useState(() => linkedManualDrivers() !== null);
  const [manualSelectedDrivers, setManualSelectedDrivers] = useState(() => linkedManualDrivers() || ["#45 Tyler Reddick"]);

  const {
    trackRaces,
    similarTrackRaces,
    allRaces,
    currentSeasonRaces,
    pastSeasonRaces,
    last5Races,
    last10Races,
    groupDrivers,
    isLoading: isFantasyDataLoading,
    error: fantasyDataError
  } = useSmartFantasyData(selectedGroup, useStarGroup);

  // Extract car numbers from currentSeasonRaces (already loaded by smart API)
  const driverCarNumbers = useMemo(() => {
    const carNumberMap = {};
    currentSeasonRaces.forEach(race => {
      if (race.driver_name && race.car_number) {
        carNumberMap[race.driver_name] = race.car_number;
      }
    });
    return carNumberMap;
  }, [currentSeasonRaces]);

  // Helper function to get car number for a driver
  const getDriverCarNumber = useCallback((driverName) => {
    return driverCarNumbers[driverName] || "";
  }, [driverCarNumbers]);

  // Helper function to format driver name with car number
  const formatDriverName = useCallback((driverName) => {
    const carNumber = getDriverCarNumber(driverName);
    return carNumber ? `#${carNumber} ${driverName}` : driverName;
  }, [getDriverCarNumber]);

  // Convert formatted driver names back to plain names for data processing
  useEffect(() => {
    const loadNextRaceData = async () => {
      try {
        setIsLoading(true);
        const nextRace = await loadJsonData("next_race_data.json");
        setNextRaceTrack(nextRace.next_race_track);
        setError(null);
      } catch (err) {
        setError("Failed to load race data");
        console.error("Error loading next race data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadNextRaceData();
  }, []);

  const selectTable = (table) => {
    setSelectedTable(table);
    trackFantasyAction('table_view_changed', {
      table_type: table,
      track: nextRaceTrack,
      group: selectedGroup.join(','),
      mode: useStarGroup ? 'star' : 'open'
    });
  };

  const tableProps = useMemo(() => ({
    groupDrivers: manualCompareMode
      ? manualSelectedDrivers.map(extractDriverName)
      : groupDrivers,
    trackRaces,
    similarTrackRaces,
    allRaces,
    currentSeasonRaces,
    pastSeasonRaces,
    last5Races,
    last10Races,
  }), [
    manualCompareMode,
    manualSelectedDrivers,
    groupDrivers,
    trackRaces,
    similarTrackRaces,
    allRaces,
    currentSeasonRaces,
    pastSeasonRaces,
    last5Races,
    last10Races,
  ]);

  // Names from a link arrive without car numbers; show them like the ones picked in the selector
  useEffect(() => {
    setManualSelectedDrivers((previous) => {
      const formatted = previous.map((driver) => formatDriverName(extractDriverName(driver)));
      return formatted.every((driver, index) => driver === previous[index]) ? previous : formatted;
    });
  }, [formatDriverName]);

  useEffect(() => {
    writeHashParam("compare", manualCompareMode ? manualSelectedDrivers.map(extractDriverName).join("|") : null);
    return () => writeHashParam("compare", null);
  }, [manualCompareMode, manualSelectedDrivers]);

  useEffect(() => {
    if (
      manualCompareMode &&
      groupDrivers.includes("Tyler Reddick") &&
      manualSelectedDrivers.length === 0
    ) {
      setManualSelectedDrivers([formatDriverName("Tyler Reddick")]);
    }
  }, [manualCompareMode, groupDrivers, manualSelectedDrivers.length, formatDriverName]);
    

  if (isLoading) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading race data...</Typography>
      </Container>
    );
  }

  // Remove custom loading indicators for now

  if (error) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h6" color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ textAlign: "center", mt: 4 }}>
      <h2>{nextRaceTrack}</h2>


      <GroupSelector
        useStarGroup={useStarGroup}
        setUseStarGroup={setUseStarGroup}
        selectedGroup={selectedGroup}
        setSelectedGroup={setSelectedGroup}
      />

    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
        mb: 2,
      }}
    >
      {/* Left: Stat Toggle (wraps onto a second row on a phone) */}
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <Chip
          label="Overview"
          color={selectedTable === "overview" ? "secondary" : "default"}
          onClick={() => {
            setSelectedTable("overview");
            trackFantasyAction('table_view_changed', {
              table_type: "overview",
              track: nextRaceTrack,
              group: selectedGroup.join(','),
              mode: useStarGroup ? 'star' : 'open'
            });
          }}
          clickable
        />
        <Chip
          label="Finish"
          color={selectedTable === "finish" ? "secondary" : "default"}
          onClick={() => {
            setSelectedTable("finish");
            trackFantasyAction('table_view_changed', {
              table_type: "finish",
              track: nextRaceTrack,
              group: selectedGroup.join(','),
              mode: useStarGroup ? 'star' : 'open'
            });
          }}
          clickable
        />
        <Chip
          label="Qualification"
          color={selectedTable === "start" ? "secondary" : "default"}
          onClick={() => {
            setSelectedTable("start");
            trackFantasyAction('table_view_changed', {
              table_type: "start",
              track: nextRaceTrack,
              group: selectedGroup.join(','),
              mode: useStarGroup ? 'star' : 'open'
            });
          }}
          clickable
        />
        <Chip
          label="Fantasy"
          color={selectedTable === "points" ? "secondary" : "default"}
          onClick={() => {
            setSelectedTable("points");
            trackFantasyAction('table_view_changed', {
              table_type: "points",
              track: nextRaceTrack,
              group: selectedGroup.join(','),
              mode: useStarGroup ? 'star' : 'open'
            });
          }}
          clickable
        />
        <Chip
          label="Rating"
          color={selectedTable === "rating" ? "secondary" : "default"}
          onClick={() => {
            setSelectedTable("rating");
            trackFantasyAction('table_view_changed', {
              table_type: "rating",
              track: nextRaceTrack,
              group: selectedGroup.join(','),
              mode: useStarGroup ? 'star' : 'open'
            });
          }}
          clickable
        />
        <Chip
          label="Advanced"
          color={selectedTable === "advanced" ? "secondary" : "default"}
          onClick={() => {
            setSelectedTable("advanced");
            trackFantasyAction('table_view_changed', {
              table_type: "advanced",
              track: nextRaceTrack,
              group: selectedGroup.join(','),
              mode: useStarGroup ? 'star' : 'open'
            });
          }}
          clickable
        />
        <Chip
          label="Ranking"
          color={selectedTable === "ranking" ? "secondary" : "default"}
          onClick={() => selectTable("ranking")}
          clickable
        />
        <Chip
          label="Consistency"
          color={selectedTable === "consistency" ? "secondary" : "default"}
          onClick={() => selectTable("consistency")}
          clickable
        />
      </Stack>

      {/* Right: Compare Toggle + Selector */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          label={manualCompareMode ? "Back to Group View" : "Manual Compare"}
          color={manualCompareMode ? "secondary" : "default"}
          onClick={() => {
            const newMode = !manualCompareMode;
            setManualCompareMode(newMode);
            setManualSelectedDrivers([]);
            trackFantasyAction('compare_mode_toggled', {
              mode: newMode ? 'manual' : 'group',
              track: nextRaceTrack
            });
          }}
          clickable
        />

        {manualCompareMode && (
          <Autocomplete
            multiple
            options={groupDrivers
              .map(formatDriverName)
              .sort((a, b) => {
                // Extract car numbers for sorting
                const carNumberA = parseInt(a.match(/#(\d+)/)?.[1] || '999');
                const carNumberB = parseInt(b.match(/#(\d+)/)?.[1] || '999');
                return carNumberA - carNumberB;
              })
            }
            value={manualSelectedDrivers}
            onChange={(e, newVal) => {
              const limitedVal = newVal.slice(0, MAX_MANUAL_DRIVERS);
              setManualSelectedDrivers(limitedVal);
              
              // Track driver selections
              const newDrivers = limitedVal.filter(driver => !manualSelectedDrivers.includes(driver));
              newDrivers.forEach(driver => {
                trackDriverSelection(extractDriverName(driver), 'fantasy_manual_compare');
              });
              
              trackFantasyAction('drivers_selected', { 
                driver_count: limitedVal.length,
                drivers: limitedVal.map(extractDriverName),
                track: nextRaceTrack
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                label="Select drivers (up to 4)"
              />
            )}
            sx={{ minWidth: { xs: 0, sm: 300 }, width: { xs: '100%', sm: 'auto' } }}
          />
        )}
      </Stack>
    </Box>


      {isFantasyDataLoading && <CircularProgress size={28} sx={{ mt: 2 }} />}
      {fantasyDataError && !isFantasyDataLoading && (
        <Typography color="error" sx={{ mt: 2 }}>
          Failed to load fantasy data: {fantasyDataError}
        </Typography>
      )}

      {selectedGroup && groupDrivers.length > 0 && renderSelectedFantasyTable(selectedTable, tableProps)}
    </Container>
  );
};

export default FantasyMainScreen;
